import React, { useState, useEffect } from 'react';
import { ReportGenerationModal } from './ReportGenerationModal';
import { ReportSchedulingModal } from './ReportSchedulingModal';
import { EmailReportModal } from './EmailReportModal';
import { ScheduledEmailsManagement } from './ScheduledEmailsManagement';
import {
  FileText,
  Download,
  Mail,
  Calendar,
  Filter,
  Search,
  Plus,
  Eye,
  Trash2,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Send
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface Report {
  id: string;
  name: string;
  description?: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  format: 'json' | 'pdf' | 'csv' | 'excel';
  generatedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  generatedAt?: string;
  completedAt?: string;
  failedAt?: string;
  errorMessage?: string;
  downloadCount: number;
  lastDownloadedAt?: string;
  fileSize?: number;
  metadata?: {
    recordCount?: number;
    processingTime?: number;
    dataRange?: {
      startDate: string;
      endDate: string;
    };
  };
  schedule?: {
    frequency: string;
    isActive: boolean;
    recipients?: string[];
  };
}

interface ReportFilters {
  search: string;
  type: string;
  status: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  generatedBy: string;
}

export const ReportsManagement: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedReportForEmail, setSelectedReportForEmail] = useState<Report | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'reports' | 'scheduled'>('reports');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState<ReportFilters>({
    search: '',
    type: '',
    status: '',
    dateRange: {
      startDate: '',
      endDate: ''
    },
    generatedBy: ''
  });

  const reportTypes = [
    { value: 'financial', label: 'Financial Report', icon: '💰' },
    { value: 'time-tracking', label: 'Time Tracking', icon: '⏰' },
    { value: 'attendance', label: 'Attendance Report', icon: '👥' },
    { value: 'client-activity', label: 'Client Activity', icon: '📊' },
    { value: 'task-performance', label: 'Task Performance', icon: '✅' },
    { value: 'payroll', label: 'Payroll Report', icon: '💵' },
    { value: 'comprehensive', label: 'Comprehensive Report', icon: '📋' }
  ];

  const statusConfig = {
    pending: { color: 'bg-gray-100 text-gray-800', icon: Clock },
    processing: { color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw },
    completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    failed: { color: 'bg-red-100 text-red-800', icon: XCircle }
  };

  const fetchReports = async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.type && { reportType: filters.type }),
        ...(filters.status && { status: filters.status }),
        ...(filters.generatedBy && { generatedBy: filters.generatedBy }),
        ...(filters.dateRange.startDate && { startDate: filters.dateRange.startDate }),
        ...(filters.dateRange.endDate && { endDate: filters.dateRange.endDate })
      });

      const response = await api.get(`/firm-admin/reports/list?${params}`);
      
      if (response.data.success) {
        setReports(response.data.data.reports || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.total || 0,
          totalPages: response.data.data.totalPages || 0
        }));
      }
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [pagination.page, pagination.limit, filters]);

  const handleFilterChange = (key: keyof ReportFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleDownloadReport = async (report: Report) => {
    try {
      // Use the report's actual format from the type & format column
      const format = report.format || 'pdf'; // fallback to pdf if format is not set

      const response = await api.get(`/firm-admin/reports/export/${report.id}`, {
        params: { format },
        responseType: 'blob'
      });

      // Determine the correct MIME type based on format
      const mimeTypes = {
        pdf: 'application/pdf',
        csv: 'text/csv',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        json: 'application/json'
      };

      const blob = new Blob([response.data], {
        type: mimeTypes[format as keyof typeof mimeTypes] || 'application/octet-stream'
      });

      // Map format to correct file extension
      const extensionMap = {
        excel: 'xlsx',
        pdf: 'pdf',
        csv: 'csv',
        json: 'json'
      };
      const fileExtension = extensionMap[format as keyof typeof extensionMap] || format;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.name || `report-${report.id}`}.${fileExtension}`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
      fetchReports(); // Refresh to update download count
    } catch (error: any) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await api.delete(`/firm-admin/reports/${reportId}`);
      toast.success('Report deleted successfully');
      fetchReports();
    } catch (error: any) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const handleBulkAction = async (action: 'delete' | 'download') => {
    if (selectedReports.length === 0) {
      toast.error('Please select reports first');
      return;
    }

    if (action === 'delete') {
      if (!window.confirm(`Are you sure you want to delete ${selectedReports.length} report(s)?`)) return;
      
      try {
        await Promise.all(
          selectedReports.map(id => api.delete(`/firm-admin/reports/${id}`))
        );
        toast.success('Reports deleted successfully');
        setSelectedReports([]);
        fetchReports();
      } catch (error: any) {
        console.error('Error deleting reports:', error);
        toast.error('Failed to delete reports');
      }
    } else if (action === 'download') {
      // Download selected reports as a zip file (would need backend support)
      toast('Bulk download feature coming soon', { icon: 'ℹ️' });
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (ms?: number): string => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const handleSendEmail = (report: Report) => {
    if (report.status !== 'completed') {
      toast.error('Only completed reports can be sent via email');
      return;
    }
    setSelectedReportForEmail(report);
    setShowEmailModal(true);
  };

  const handleEmailSent = () => {
    fetchReports(); // Refresh to update download count
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-2 text-lg">Loading reports...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Reports Management</h1>
              <p className="text-gray-600">Generate, schedule, and manage your business reports</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate Report
              </button>
              <button
                onClick={() => setShowScheduleModal(true)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Report
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('reports')}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'reports'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className={`mr-2 h-5 w-5 ${
                  activeTab === 'reports' ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`} />
                Generated Reports
              </button>
              <button
                onClick={() => setActiveTab('scheduled')}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'scheduled'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Mail className={`mr-2 h-5 w-5 ${
                  activeTab === 'scheduled' ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                }`} />
                Scheduled Emails
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'scheduled' ? (
          <ScheduledEmailsManagement onCreateNew={() => setShowScheduleModal(true)} />
        ) : (
          <>
            {/* Filters and Search */}
            <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </button>
              
              <button
                onClick={fetchReports}
                disabled={refreshing}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={filters.dateRange.startDate}
                    onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={filters.dateRange.endDate}
                    onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Generated By</label>
                  <input
                    type="text"
                    placeholder="User name..."
                    value={filters.generatedBy}
                    onChange={(e) => handleFilterChange('generatedBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedReports.length > 0 && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedReports.length} report(s) selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkAction('download')}
                  className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="flex items-center px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </button>
                <button
                  onClick={() => setSelectedReports([])}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reports Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {reports.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedReports.length === reports.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedReports(reports.map(r => r.id));
                            } else {
                              setSelectedReports([]);
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Report Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type & Format
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Generated By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reports.map((report) => {
                      const statusInfo = statusConfig[report.status] || statusConfig.pending;
                      const StatusIcon = statusInfo.icon;
                      const reportType = reportTypes.find(t => t.value === report.type);

                      return (
                        <tr key={report.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedReports.includes(report.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedReports([...selectedReports, report.id]);
                                } else {
                                  setSelectedReports(selectedReports.filter(id => id !== report.id));
                                }
                              }}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-primary-600" />
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{report.name}</div>
                                {report.description && (
                                  <div className="text-sm text-gray-500 mt-1">{report.description}</div>
                                )}
                                {report.metadata?.dataRange && (
                                  <div className="text-xs text-gray-400 mt-1">
                                    {new Date(report.metadata.dataRange.startDate).toLocaleDateString()} - {' '}
                                    {new Date(report.metadata.dataRange.endDate).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <span className="text-lg mr-2">{reportType?.icon}</span>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {reportType?.label || report.type}
                                </div>
                                <div className="text-xs text-gray-500 uppercase">
                                  {report.format}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                <StatusIcon className={`w-3 h-3 mr-1 ${report.status === 'processing' ? 'animate-spin' : ''}`} />
                                {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                              </span>
                            </div>
                            {report.status === 'failed' && report.errorMessage && (
                              <div className="text-xs text-red-600 mt-1" title={report.errorMessage}>
                                {report.errorMessage.length > 30
                                  ? `${report.errorMessage.substring(0, 30)}...`
                                  : report.errorMessage}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {report.generatedBy.firstName} {report.generatedBy.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {report.generatedAt && new Date(report.generatedAt).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs text-gray-500 space-y-1">
                              {report.fileSize && (
                                <div>Size: {formatFileSize(report.fileSize)}</div>
                              )}
                              {report.metadata?.recordCount && (
                                <div>Records: {report.metadata.recordCount.toLocaleString()}</div>
                              )}
                              {report.metadata?.processingTime && (
                                <div>Time: {formatDuration(report.metadata.processingTime)}</div>
                              )}
                              {report.downloadCount > 0 && (
                                <div>Downloads: {report.downloadCount}</div>
                              )}
                              {report.schedule?.isActive && (
                                <div className="flex items-center text-blue-600">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  Scheduled
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {report.status === 'completed' && (
                                <>
                                  <button
                                    onClick={() => handleDownloadReport(report)}
                                    className="text-primary-600 hover:text-primary-900 p-1"
                                    title={`Download ${(report.format || 'PDF').toUpperCase()}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleSendEmail(report)}
                                    className="text-blue-600 hover:text-blue-900 p-1"
                                    title="Send via Email"
                                  >
                                    <Send className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {report.status === 'processing' && (
                                <span className="text-gray-400 text-xs">Generating...</span>
                              )}
                              {report.status === 'failed' && (
                                <button
                                  onClick={() => {/* TODO: Implement retry functionality */}}
                                  className="text-orange-600 hover:text-orange-900 p-1"
                                  title="Retry Generation"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteReport(report.id)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Delete Report"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {(pagination.page - 1) * pagination.limit + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{pagination.total}</span>{' '}
                      results
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-700">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Found</h3>
              <p className="text-gray-500 mb-4">
                {filters.search || filters.type || filters.status
                  ? 'No reports match your current filters.'
                  : 'Generate your first report to get started.'}
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Generate Report
              </button>
            </div>
          )}
        </div>
          </>
        )}

        {/* Modals */}
        <ReportGenerationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onReportGenerated={fetchReports}
        />

        <ReportSchedulingModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onScheduleCreated={fetchReports}
        />

        {selectedReportForEmail && (
          <EmailReportModal
            isOpen={showEmailModal}
            onClose={() => {
              setShowEmailModal(false);
              setSelectedReportForEmail(null);
            }}
            reportId={selectedReportForEmail.id}
            reportName={selectedReportForEmail.name}
            onEmailSent={handleEmailSent}
          />
        )}
    </div>
  );
};
